import { useQuery } from '@tanstack/react-query';
import { useId, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import {
  fetchChatCreatedFiles,
  type ChatCreatedFile,
  type ChatFileKeptReason,
} from '@/lib/api/chat-created-files.js';

/** How many file names a list shows before it folds the rest behind "Show all". */
const COLLAPSED_FILE_COUNT = 5;

/** The short note beside a kept file. Every reason maps; the API module reads an unknown one as `shared`. */
const KEPT_LABELS: Record<ChatFileKeptReason, string> = {
  shared: 'shared elsewhere',
  used_in_other_chat: 'shared elsewhere',
  created_by_someone_else: 'added by someone else',
  not_allowed: "you can't delete it",
};

/** "1 file" or "3 files". */
function filesLabel(count: number): string {
  return `${count} file${count === 1 ? '' : 's'}`;
}

/**
 * The delete confirmation for one chat (MEDA-1150). When the chat has files born in it, or used by an
 * automation that runs in it (MEDA-1324), that the author may take down, it offers "Also delete N
 * files", unchecked by default. Those files
 * are archived, not erased, so they can be restored from Artifacts → Archived. Files whose audience
 * reaches past the chat, or that are not the author's, are listed as kept.
 *
 * The file check never blocks the delete: while it loads, or when it fails, the chat can still be
 * deleted and its files simply stay.
 */
export function DeleteChatDialog({
  conversationId,
  title,
  busy,
  onConfirm,
  onClose,
}: {
  conversationId: string;
  title: string;
  busy: boolean;
  onConfirm: (artifactIds: string[]) => void;
  onClose: () => void;
}): JSX.Element {
  const [alsoDeleteFiles, setAlsoDeleteFiles] = useState(false);
  const checkboxId = useId();
  /**
   * Answered fresh for every opening: `gcTime: 0` drops the answer when the dialog closes, and only a
   * settled, successful check offers anything, so rows cached by an earlier opening can never be
   * submitted while this one's check is pending or has failed.
   */
  const filesQuery = useQuery({
    queryKey: ['chat-created-files', conversationId],
    queryFn: () => fetchChatCreatedFiles(conversationId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const files =
    filesQuery.isSuccess && !filesQuery.isFetching ? filesQuery.data : [];
  const deletable = files.filter((file) => file.deletable);
  const kept = files.filter((file) => !file.deletable);
  const filesSource = deletable.some((file) => file.fromAutomation)
    ? 'from this chat and its automations'
    : 'created in this chat';

  function confirm(): void {
    onConfirm(alsoDeleteFiles ? deletable.map((file) => file.artifactId) : []);
  }

  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && !busy && onClose()}
      showCloseButton={!busy}
      title="Delete chat?"
      description={`"${title}" and its messages will be deleted. This can't be undone.`}
    >
      {filesQuery.isFetching && (
        <p className="text-sm text-muted-foreground">
          Checking files created in this chat…
        </p>
      )}
      {filesQuery.isError && (
        <p role="alert" className="text-sm text-muted-foreground">
          Files created in this chat couldn't be checked. Deleting the chat
          keeps them in Artifacts.
        </p>
      )}
      {deletable.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <Checkbox
              id={checkboxId}
              checked={alsoDeleteFiles}
              disabled={busy}
              onCheckedChange={(checked) =>
                setAlsoDeleteFiles(checked === true)
              }
              className="mt-0.5"
            />
            <label htmlFor={checkboxId} className="text-sm">
              Also delete {filesLabel(deletable.length)} {filesSource}
              <span className="block text-muted-foreground">
                They move to Archived in Artifacts, where you can restore them.
              </span>
            </label>
          </div>
          <FileNames files={deletable} label="Files that can be deleted" />
        </div>
      )}
      {kept.length > 0 && (
        <div className="mt-3 flex flex-col gap-1">
          <p className="text-sm">Kept: {filesLabel(kept.length)}</p>
          <FileNames files={kept} label="Files that are kept" />
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={confirm} disabled={busy}>
          {busy ? 'Deleting…' : 'Delete chat'}
        </Button>
      </div>
    </AppDialog>
  );
}

/** A short list of file names, folded after {@link COLLAPSED_FILE_COUNT} with a "Show all" toggle. */
function FileNames({
  files,
  label,
}: {
  files: ChatCreatedFile[];
  label: string;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? files : files.slice(0, COLLAPSED_FILE_COUNT);
  const hiddenCount = files.length - shown.length;
  return (
    <div className="pl-6">
      <ul
        aria-label={label}
        className="flex max-h-40 flex-col gap-0.5 overflow-y-auto text-sm text-muted-foreground"
      >
        {shown.map((file) => (
          <li key={file.artifactId} className="truncate" title={file.name}>
            {file.name}
            {file.keptReason && ` — ${KEPT_LABELS[file.keptReason]}`}
            {!file.keptReason &&
              file.fromAutomation &&
              " — used by this chat's automation"}
          </li>
        ))}
      </ul>
      {hiddenCount > 0 && (
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0"
          onClick={() => setExpanded(true)}
        >
          Show all {files.length}
        </Button>
      )}
    </div>
  );
}
